import { Component, ElementRef, QueryList, ViewChild, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { TargetDrawer } from '../TargetDrawer';

@Component({
  selector: 'app-vec-tarc',
  templateUrl: './vec-tarc.component.html',
  styleUrls: ['./vec-tarc.component.scss']
})
export class VecTarcComponent implements OnInit, OnDestroy {
  @ViewChild('canvasContainer', { static: false }) canvasContainer!: ElementRef<HTMLDivElement>;

  shotsByTarget: { [targetID: string]: any[] } = {};
  targetTypeByTarget: { [targetID: string]: string } = {};
  latestTargets: any[] = [];
  shotsResults: any[] = [];
  shotsResultsByTarget: { [key: string]: any } = {}; // Main object for displaying results
  intervalId: any;
  viewMode: 'all' | 'first5' | 'second5' = 'all';
  gridRows = 2;
  targetsPerRow = 5;
  selectedShotGroupStart: number | 'last' | 'all' = 'all';
  readonly shotGroups = Array.from({ length: 6 }, (_, index) => ({
    start: index * 10 + 1,
    end: index * 10 + 10
  }));
  targetDisplayMode: 'standard' | 'rotate-pages' = 'rotate-pages';
  targetRotationSeconds = 15;
  private targetRotationTimer: any;
  private targetRotationPage = 0;
  private loadingTargetIds = new Set<string>();
  private loadingShotTargetIds = new Set<string>();
  private latestTargetsLoading = false;
  settingsLoaded = false;

  // Optionally, collect all target data in an array:
  targetDataById: { [key: string]: any } = {};

  targetsNames: any[] = [];
  canvasesByOrder: { [order: number]: HTMLCanvasElement } = {};
  wrappersByOrder: { [order: number]: HTMLDivElement } = {};
  headingsByOrder: { [order: number]: HTMLDivElement } = {};

  constructor(private http: HttpClient, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.loadDisplaySettings(() => {
      this.loadTargetsNames();
      this.startTargetRotation();
      this.intervalId = setInterval(() => {
        this.loadLatestTargets();
      }, 5000);
    });
  }

  ngOnDestroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    this.stopTargetRotation();
  }

  loadTargetsNames(): void {
    const apiUrl = 'https://testapi.ovh/api/strelstvo/targets-names';
    const token = localStorage.getItem('access_token');
    const unitId = localStorage.getItem('unit_id') || '1';
    const headers = token ? new HttpHeaders({ Authorization: `Bearer ${token}` }) : undefined;
    const params = new HttpParams().set('unit_id', unitId);

    this.http.get<any[]>(apiUrl, { headers, params }).subscribe(
      (targets) => {
        this.targetsNames = Array.isArray(targets)
          ? this.uniqueTargetsByOrder(targets)
          : [];
        this.createCanvasesByOrder();
        // Load target data after all canvases exist to avoid drawing into an undefined canvas.
        this.loadLatestTargets();
      },
      (error) => {
        console.error('Error fetching targets names:', error);
        this.targetsNames = [];
      }
    );    
    this.updateCanvasVisibility();
  }

  createCanvasesByOrder(): void {
    const container = this.canvasContainer?.nativeElement;
    if (!container) return;
    container.innerHTML = ''; // Clear previous content if any
    this.canvasesByOrder = {}; // Reset canvases map
    this.wrappersByOrder = {}; // Reset wrappers map
    this.headingsByOrder = {};

    this.targetsNames.forEach(target => {
    if (target.TargetOrder && target.TargetOrder !== 0) {
        // Create wrapper div
        const wrapper = document.createElement('div');
        wrapper.className = 'target-canvas-wrapper';
        this.wrappersByOrder[target.TargetOrder] = wrapper;

        // Create heading
        const heading = document.createElement('div');
        heading.className = 'target-heading';
        heading.textContent = this.getTargetHeading(target);
        this.headingsByOrder[target.TargetOrder] = heading;

        // Create canvas
        const canvas = document.createElement('canvas');
        canvas.width = 400;
        canvas.height = 400;
        canvas.id = `Target${target.TargetOrder}`;
        canvas.className = 'target-canvas';

    this.canvasesByOrder[target.TargetOrder] = canvas;

        // Append heading and canvas to wrapper
        wrapper.appendChild(heading);
        wrapper.appendChild(canvas);

        // Append wrapper to container
        container.appendChild(wrapper);
    }
    });
  }

  getTargetHeading(target: any): string {
    const order = target?.TargetOrder ?? '-';
    const shooter = target?.member_full_name || target?.full_name || target?.shooter_name;
    return shooter ? `Tarča ${order} - ${shooter}` : `Tarča ${order}`;
  }

  private updateTargetHeadings(): void {
    this.visibleTargets.forEach(target => {
      const heading = this.headingsByOrder[target.TargetOrder];
      if (heading) heading.textContent = this.getTargetHeading(target);
    });
  }

  loadLatestTargets(): void {
    if (this.latestTargetsLoading) return;
    this.latestTargetsLoading = true;

    const apiUrl = 'https://testapi.ovh/api/strelstvo/latest-targets';
    const token = localStorage.getItem('access_token');
    const unitId = localStorage.getItem('unit_id') || '1';
    const headers = token ? new HttpHeaders({ Authorization: `Bearer ${token}` }) : undefined;
    const params = new HttpParams().set('unit_id', unitId);

    this.http.get<any[]>(apiUrl, { headers, params }).subscribe(
      (targetsData) => {
        this.latestTargets = Array.isArray(targetsData)
          ? this.uniqueTargetsByOrder(targetsData)
          : [];

        this.updateCanvasVisibility();
        this.updateTargetHeadings();

        this.visibleTargets.forEach(target => {
          const canvas = this.canvasesByOrder[target.TargetOrder];
          if (target.targetID && target.targetID !== 0) {
            this.loadTargetById(target.targetID, canvas, true);
          } else if (canvas) {
            this.drawAllShots([], '', canvas);
          }
        });

        this.preloadHiddenTargets();
        this.latestTargetsLoading = false;
      },
      (error) => {
        console.error('Error fetching latest targets:', error);
        this.latestTargets = [];
        this.latestTargetsLoading = false;
      }
    );
  }  

  loadTargetById(targetID: string, canvas?: HTMLCanvasElement, refreshShots = false): void {
    const cachedTarget = this.targetDataById[targetID];
    if (cachedTarget && !refreshShots) {
      this.shotsResultsByTarget[targetID] = cachedTarget;
      this.loadShotsForTarget(cachedTarget.targetID, cachedTarget.target_type, cachedTarget.TargetOrder, canvas, refreshShots);
      return;
    }

    if (this.loadingTargetIds.has(String(targetID))) return;
    this.loadingTargetIds.add(String(targetID));

    const apiUrl = 'https://testapi.ovh/api/strelstvo/targets';
    const token = localStorage.getItem('access_token');
    const unitId = localStorage.getItem('unit_id') || '1';
    const headers = token ? new HttpHeaders({ Authorization: `Bearer ${token}` }) : undefined;
    let params = new HttpParams().set('targetID', targetID).set('unit_id', unitId);

    this.http.get<any[]>(apiUrl, { params, headers }).subscribe(
      (targetsData) => {
        if (Array.isArray(targetsData) && targetsData.length > 0) {
          const target = targetsData[0];
          this.targetDataById[targetID] = target;
          // Store or process target as needed
          this.shotsResultsByTarget[target.targetID] = target;
          // Now call loadShotsForTarget with all needed info
          this.loadShotsForTarget(target.targetID, target.target_type, target.TargetOrder, canvas, refreshShots);
        }
        this.loadingTargetIds.delete(String(targetID));
      },
      (error) => {
        console.error('Error fetching target:', error);
        this.loadingTargetIds.delete(String(targetID));
      }
    );
  }

  // Helper to load shots for a targetID and run a callback with the shots
  loadShotsForTarget(targetID: string, targetType: string, targetOrder: number, canvas?: HTMLCanvasElement, refresh = false): void {
    const cachedShots = this.shotsByTarget[String(targetID)];
    if (cachedShots && !refresh) {
      this.targetTypeByTarget[String(targetID)] = targetType;
      if (canvas) this.drawAllShots(this.getSelectedShotGroup(cachedShots), targetType, canvas);
      return;
    }

    if (this.loadingShotTargetIds.has(String(targetID))) return;
    this.loadingShotTargetIds.add(String(targetID));

    const apiUrl = 'https://testapi.ovh/api/strelstvo/shots';
    const token = localStorage.getItem('access_token');
    const unitId = localStorage.getItem('unit_id') || '1';
    const headers = token ? new HttpHeaders({ Authorization: `Bearer ${token}` }) : undefined;
    let params = new HttpParams().set('targetID', targetID).set('unit_id', unitId);

    this.http.get<any[]>(apiUrl, { params, headers }).subscribe(
      (shotsData) => {
        const shots = Array.isArray(shotsData) ? shotsData : [];
        this.shotsByTarget[targetID] = shots;
        this.targetTypeByTarget[targetID] = targetType;
        // console.log(`[loadShotsForTarget] targetID: ${targetID}, shots:`, shots);

        // console.log(`canvas ${canvas} for TargetOrder ${targetOrder}`);
        if (canvas) {
          this.drawAllShots(this.getSelectedShotGroup(shots), targetType, canvas);
        } else {
          console.warn(`No canvas found for TargetOrder: ${targetOrder}`);
        }
        this.loadingShotTargetIds.delete(String(targetID));
      },
      (error) => {
        console.error('Error fetching shots for target:', error);
        this.loadingShotTargetIds.delete(String(targetID));
      }
    );
  }  

  private preloadHiddenTargets(): void {
    const visibleIds = new Set(this.visibleTargets.map(target => String(target.targetID)));
    this.latestTargets.forEach(target => {
      const targetID = String(target.targetID || '');
      if (targetID && targetID !== '0' && !visibleIds.has(targetID)) {
        this.loadTargetById(targetID);
      }
    });
  }

  hasValidGroupData(targetID: number): boolean {
    const result = this.shotsResultsByTarget[targetID];
    if (!result) return false;

    // Check if any group has a valid score
    return ['S10', 'S20', 'S30', 'S40', 'S50', 'S60'].some(
      (group) => result[group] !== null && result[group] > 0
    );
  }

  isGroupVisible(group: string): boolean {
    return true;
  }

  setShotGroup(value: number | string): void {
    if (value === 'last' || value === 'all') {
      this.selectedShotGroupStart = value;
    } else {
    const parsed = Number(value);
      this.selectedShotGroupStart = this.shotGroups.some(group => group.start === parsed) ? parsed : 'last';
    }

    this.visibleTargets.forEach(target => {
      const targetID = target.targetID;
      const canvas = this.canvasesByOrder[target.TargetOrder];
      if (targetID && canvas) {
        this.drawAllShots(
          this.getSelectedShotGroup(this.shotsByTarget[targetID] || []),
          this.targetTypeByTarget[targetID] || target.target_type || '',
          canvas
        );
      }
    });
  }

  setTargetDisplayMode(value: string): void {
    this.targetDisplayMode = value === 'rotate-pages' ? 'rotate-pages' : 'standard';
    this.targetRotationPage = 0;

    if (this.targetDisplayMode === 'rotate-pages') {
      this.startTargetRotation();
    } else {
      this.stopTargetRotation();
    }

    this.refreshVisibleTargetCanvases();
    this.saveDisplaySettings();
  }

  private startTargetRotation(): void {
    this.stopTargetRotation();
    this.targetRotationTimer = setInterval(() => {
      const configuredTargets = this.targetsNames.length ? this.targetsNames : this.latestTargets;
      const targetsPerPage = Math.max(1, this.gridRows * this.targetsPerRow);
      const pageCount = Math.max(1, Math.ceil(configuredTargets.length / targetsPerPage));
      this.targetRotationPage = (this.targetRotationPage + 1) % pageCount;
      this.refreshVisibleTargetCanvases();
    }, this.targetRotationSeconds * 1000);
  }

  private stopTargetRotation(): void {
    if (this.targetRotationTimer) {
      clearInterval(this.targetRotationTimer);
      this.targetRotationTimer = null;
    }
  }

  private refreshVisibleTargetCanvases(): void {
    this.updateCanvasVisibility();
    this.updateTargetHeadings();

    this.visibleTargets.forEach(target => {
      const canvas = this.canvasesByOrder[target.TargetOrder];
      if (target.targetID && canvas) {
        this.loadTargetById(target.targetID, canvas);
      }
    });
  }

  private getSelectedShotGroup(shots: any[]): any[] {
    if (this.selectedShotGroupStart === 'all') {
      return shots;
    }

    if (this.selectedShotGroupStart === 'last') {
      const lastShotNumber = shots.reduce((highest, shot, index) => {
        const order = Number(shot?.OrderOfShot);
        const shotNumber = Number.isFinite(order) && order > 0 ? order : index + 1;
        return Math.max(highest, shotNumber);
      }, 0);
      const lastGroupStart = lastShotNumber > 0 ? Math.floor((lastShotNumber - 1) / 10) * 10 + 1 : 1;
      return shots.filter((shot, index) => {
        const order = Number(shot?.OrderOfShot);
        const shotNumber = Number.isFinite(order) && order > 0 ? order : index + 1;
        return shotNumber >= lastGroupStart && shotNumber <= lastGroupStart + 9;
      });
    }

    const start = this.selectedShotGroupStart as number;
    const end = start + 9;
    return shots.filter((shot, index) => {
      const order = Number(shot?.OrderOfShot);
      const shotNumber = Number.isFinite(order) && order > 0 ? order : index + 1;
      return shotNumber >= start && shotNumber <= end;
    });
  }

  get visibleTargets() {
    const configuredTargets = this.targetsNames.length ? this.targetsNames : this.latestTargets;
    const mergedTargets = configuredTargets
      .map(configuredTarget => {
        const latestTarget = this.latestTargets.find(
          target => target.TargetOrder === configuredTarget.TargetOrder,
        );
        return latestTarget
          ? { ...configuredTarget, ...latestTarget, TargetOrder: configuredTarget.TargetOrder }
          : configuredTarget;
      });

    if (this.targetDisplayMode === 'rotate-pages' && mergedTargets.length > 0) {
      const targetsPerPage = Math.max(1, this.gridRows * this.targetsPerRow);
      const start = (this.targetRotationPage * targetsPerPage) % mergedTargets.length;
      return mergedTargets.slice(start, start + targetsPerPage);
    }

    return mergedTargets.slice(0, this.gridRows * this.targetsPerRow);
  }

  private uniqueTargetsByOrder(targets: any[]): any[] {
    const targetsByOrder = new Map<number, any>();

    targets.forEach(target => {
      const order = Number(target.TargetOrder);
      const existingTarget = targetsByOrder.get(order);
      const targetID = Number(target.targetID || 0);
      const existingTargetID = Number(existingTarget?.targetID || 0);

      if (!existingTarget || targetID > existingTargetID) {
        targetsByOrder.set(order, target);
      }
    });

    return Array.from(targetsByOrder.values()).sort(
      (first, second) => Number(first.TargetOrder) - Number(second.TargetOrder),
    );
  }

  setGridRows(value: number | string): void {
    this.gridRows = this.clampGridValue(value, 1, 10);
    this.targetRotationPage = 0;
    this.refreshVisibleTargetCanvases();
    this.saveDisplaySettings();
  }

  setTargetsPerRow(value: number | string): void {
    this.targetsPerRow = this.clampGridValue(value, 1, 12);
    this.targetRotationPage = 0;
    this.refreshVisibleTargetCanvases();
    this.saveDisplaySettings();
  }

  setTargetRotationSeconds(value: number | string): void {
    this.targetRotationSeconds = this.clampGridValue(value, 1, 300);
    if (this.targetDisplayMode === 'rotate-pages') {
      this.startTargetRotation();
    }
    this.saveDisplaySettings();
  }

  onSaveDisplaySettings(): void {
    this.saveDisplaySettings();
  }

  private loadDisplaySettings(onLoaded: () => void): void {
    const token = localStorage.getItem('access_token');
    const unitId = localStorage.getItem('unit_id') || '1';
    const headers = token ? new HttpHeaders({ Authorization: `Bearer ${token}` }) : undefined;
    const params = new HttpParams().set('unit_id', unitId);

    this.http.get<any>('https://testapi.ovh/api/strelstvo/target-display-settings', { headers, params }).subscribe({
      next: settings => {
        if (settings) {
          this.gridRows = this.clampGridValue(settings.grid_rows, 1, 10);
          this.targetsPerRow = this.clampGridValue(settings.targets_per_row, 1, 12);
          this.targetDisplayMode = settings.target_display_mode === 'standard' ? 'standard' : 'rotate-pages';
          if (Number.isFinite(Number(settings.target_rotation_seconds))) {
            this.targetRotationSeconds = this.clampGridValue(settings.target_rotation_seconds, 1, 300);
          }
          this.updateCanvasVisibility();
        }
        this.settingsLoaded = true;
        this.cdr.detectChanges();
        onLoaded();
      },
      error: error => {
        console.error('Error loading target display settings:', error);
        this.settingsLoaded = true;
        this.cdr.detectChanges();
        onLoaded();
      }
    });
  }

  saveDisplaySettings(): void {
    const token = localStorage.getItem('access_token');
    const unitId = localStorage.getItem('unit_id') || '1';
    const headers = token ? new HttpHeaders({ Authorization: `Bearer ${token}` }) : undefined;
    const params = new HttpParams().set('unit_id', unitId);

    this.http.put('https://testapi.ovh/api/strelstvo/target-display-settings', {
      grid_rows: this.gridRows,
      targets_per_row: this.targetsPerRow,
      target_display_mode: this.targetDisplayMode,
      target_rotation_seconds: this.targetRotationSeconds
    }, { headers, params }).subscribe({
      error: error => console.error('Error saving target display settings:', error)
    });
  }

  private clampGridValue(value: number | string, min: number, max: number): number {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return min;
    return Math.min(max, Math.max(min, Math.floor(parsed)));
  }

  updateCanvasVisibility(): void {
    this.canvasContainer?.nativeElement.style.setProperty('--target-columns', String(this.targetsPerRow));

    // Hide all wrappers first
    Object.values(this.wrappersByOrder).forEach(wrapper => {
      wrapper.style.display = 'none';
    });
    // Show only visible targets' wrappers
    this.visibleTargets.forEach(target => {
      const wrapper = this.wrappersByOrder[target.TargetOrder];
      if (wrapper) {
        wrapper.style.display = '';
      }
    });
  }

  drawEmptyPistolTarget(canvas: HTMLCanvasElement): void {
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    TargetDrawer.drawPistolTarget(canvas, [], 1, 0, 0);
  }

  drawAllShots(shots: any[], targetType: string, canvas: HTMLCanvasElement): void {
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // console.log(`[drawAllShots] targetType: ${targetType}, shots:`, shots);

    const normalizedType = String(targetType || '').trim().toLowerCase() || 'air rifle';
    const lastShotIndex = shots.length - 1;

    if (normalizedType.startsWith('pistol')) {
      TargetDrawer.drawPistolTarget(canvas, shots, 1, 0, shots.length, lastShotIndex);
    } else if (normalizedType === 'air serial') {
      TargetDrawer.drawSerialAirRifleTarget(canvas, shots, 2.5, 0, shots.length, lastShotIndex);
    } else if (normalizedType === 'air rifle') {
      TargetDrawer.drawFullAirRifleTarget(canvas, shots, 4, 0, shots.length, lastShotIndex);
    } else {
      // Automatic targets may not have target_type metadata, but their shots still need a target.
      TargetDrawer.drawFullAirRifleTarget(canvas, shots, 4, 0, shots.length, lastShotIndex);
    }
  }
}
